<template>
  <div class="mx-3 my-3">
    <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 my-2">
      <div>
        <label class="text-sm text-gray block mb-1">Start Date</label>
        <VueDatePicker
          v-model="filters.startDate"
          format="dd/MM/yyyy"
          placeholder="dd/mm/yyyy"
          auto-apply
          :enable-time-picker="false"
        />
      </div>
      <div>
        <label class="text-sm text-gray block mb-1">End Date</label>
        <VueDatePicker
          v-model="filters.endDate"
          format="dd/MM/yyyy"
          placeholder="dd/mm/yyyy"
          auto-apply
          :enable-time-picker="false"
        />
      </div>
      <div class="flex items-end gap-2">
        <button class="btn-primary px-3 py-2 rounded-sm text-white" type="button" @click="fetch">
          <i class="fa fa-search mr-1"></i> Search
        </button>
        <button
          class="px-3 py-2 rounded-sm border border-gray-300 text-gray-700 hover:border-primary hover:text-primary"
          type="button"
          @click="resetFilters"
        >
          <i class="fa fa-undo mr-1"></i> Reset
        </button>
      </div>
    </div>

    <div v-if="loading" class="h-0.5 bg-primary animate-pulse mb-2"></div>

    <div v-if="stats" class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 m-2">
      <div v-for="card in statCards" :key="card.key" class="p-3 shadow bg-white rounded-md">
        <p class="text-xs text-gray">{{ card.label }}</p>
        <p class="text-lg font-bold text-primary">{{ formatCount(stats[card.key]) }}</p>
      </div>
    </div>

    <div v-if="trendRows.length > 0" class="mx-1 my-3">
      <p class="text-sm font-bold mb-2">Daily trend</p>
      <table class="stats-table min-w-full leading-normal">
        <thead>
        <tr class="border-b border-t border-gray-200">
          <th class="t-header">Date</th>
          <th class="t-header">SMS</th>
          <th class="t-header">Email</th>
          <th class="t-header">Sent</th>
          <th class="t-header">Failed</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, idx) in trendRows"
          :key="row.date"
          :class="idx % 2 ? 'border-t border-gray-200 hover:bg-gray-50' : 'border-t border-gray-200 bg-gray-50'"
        >
          <td class="t-body">{{ formatTrendDate(row.date) }}</td>
          <td class="t-body">{{ formatCount(row.sms) }}</td>
          <td class="t-body">{{ formatCount(row.email) }}</td>
          <td class="t-body">{{ formatCount(row.sent) }}</td>
          <td class="t-body">{{ formatCount(row.failed) }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-if="topReceivers.length > 0" class="mx-1 my-3">
      <p class="text-sm font-bold mb-2">Top receivers</p>
      <table class="stats-table min-w-full leading-normal">
        <thead>
        <tr class="border-b border-t border-gray-200">
          <th class="t-header">#</th>
          <th class="t-header">Receiver</th>
          <th class="t-header">Type</th>
          <th class="t-header">Count</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, idx) in topReceivers"
          :key="row.receiver + '-' + row.type + '-' + idx"
          :class="idx % 2 ? 'border-t border-gray-200 hover:bg-gray-50' : 'border-t border-gray-200 bg-gray-50'"
        >
          <td class="t-body">{{ idx + 1 }}</td>
          <td class="t-body truncate max-w-xs" :title="row.receiver">{{ row.receiver }}</td>
          <td class="t-body">{{ row.type || "—" }}</td>
          <td class="t-body">{{ formatCount(row.count) }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-if="topErrors.length > 0" class="mx-1 my-3">
      <p class="text-sm font-bold mb-2">Top errors</p>
      <table class="stats-table min-w-full leading-normal">
        <thead>
        <tr class="border-b border-t border-gray-200">
          <th class="t-header">#</th>
          <th class="t-header">Error</th>
          <th class="t-header">Type</th>
          <th class="t-header">Count</th>
        </tr>
        </thead>
        <tbody>
        <tr
          v-for="(row, idx) in topErrors"
          :key="'err-' + idx"
          :class="idx % 2 ? 'border-t border-gray-200 hover:bg-gray-50' : 'border-t border-gray-200 bg-gray-50'"
        >
          <td class="t-body">{{ idx + 1 }}</td>
          <td class="t-body truncate max-w-md" :title="row.error">{{ truncateText(row.error, 120) }}</td>
          <td class="t-body">{{ row.type || "—" }}</td>
          <td class="t-body">{{ formatCount(row.count) }}</td>
        </tr>
        </tbody>
      </table>
    </div>

    <div v-if="!loading && !stats" class="mx-1 my-3 bg-gray-100 text-center py-3 rounded">
      <span class="text-sm">No statistics available</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref } from "vue";
import moment from "moment";
import { api } from "@/app/services/api.service";
import { useToast } from "@/composables";

const STATISTICS_PATH = "notifications/statistics";

interface TrendRow {
  date: string
  sms: number
  email: number
  sent: number
  failed: number
}

interface TopReceiver {
  receiver: string
  type?: string
  count: number
}

interface TopError {
  error: string
  type?: string
  count: number
}

const { errorToast, warningToast } = useToast();

const loading = ref(false);
const stats = ref<Record<string, number> | null>(null);
const trendRows = ref<TrendRow[]>([]);
const topReceivers = ref<TopReceiver[]>([]);
const topErrors = ref<TopError[]>([]);
const filters = ref<{ startDate: Date | null; endDate: Date | null }>({
  startDate: null,
  endDate: null,
});

const statCards = [
  { key: "totalNotifications", label: "Total notifications" },
  { key: "totalSms", label: "Total SMS" },
  { key: "totalEmail", label: "Total email" },
  { key: "totalPending", label: "Pending" },
  { key: "totalSent", label: "Sent" },
  { key: "totalFailed", label: "Failed" },
  { key: "totalUgpass", label: "UGPASS" },
  { key: "totalInstacom", label: "Instacom" },
  { key: "totalInfobip", label: "Infobip" },
  { key: "totalSmtp", label: "SMTP" },
];

function trendDataValue(data: Record<string, unknown> | null, keys: string[]) {
  if (!data || typeof data !== "object") return 0;
  for (const key of keys) {
    if (data[key] != null) return Number(data[key]) || 0;
    const upper = key.toUpperCase();
    if (data[upper] != null) return Number(data[upper]) || 0;
    const lower = key.toLowerCase();
    if (data[lower] != null) return Number(data[lower]) || 0;
  }
  return 0;
}

const formatCount = (value: unknown) => {
  if (value == null || Number.isNaN(Number(value))) return "0";
  return Number(value).toLocaleString();
};

const truncateText = (value: unknown, maxLen: number) => {
  if (value == null || value === "") return "—";
  const s = String(value).replace(/\s+/g, " ").trim();
  if (s.length <= maxLen) return s;
  return s.substring(0, maxLen) + "…";
};

const toApiDate = (value: Date | null) => {
  if (!value) return null;
  const m = moment(value);
  return m.isValid() ? m.format("YYYY-MM-DD") : null;
};

const formatTrendDate = (dateStr: string) => {
  if (!dateStr || dateStr === "—") return "—";
  const m = moment(dateStr, ["YYYY-MM-DD", "DD/MM/YYYY"], true);
  return m.isValid() ? m.format("DD/MM/YYYY") : dateStr;
};

const clearResults = () => {
  stats.value = null;
  trendRows.value = [];
  topReceivers.value = [];
  topErrors.value = [];
};

const normalizeTrend = (trend: unknown): TrendRow[] => {
  if (!Array.isArray(trend)) return [];
  return trend.map((entry: { date?: string; data?: Record<string, unknown> }) => {
    const data = entry?.data ?? {};
    return {
      date: entry?.date ?? "—",
      sms: trendDataValue(data, ["SMS", "sms"]),
      email: trendDataValue(data, ["EMAIL", "email"]),
      sent: trendDataValue(data, ["SENT", "sent"]),
      failed: trendDataValue(data, ["FAILED", "failed"]),
    };
  });
};

const unwrapPayload = (res: { data: unknown }) => {
  const d = res.data as Record<string, unknown>;
  if (d && typeof d === "object" && Object.prototype.hasOwnProperty.call(d, "data") && d.data != null) {
    return d.data;
  }
  return d;
};

const buildRequestParams = () => {
  const params: Record<string, string> = {};
  const startDate = toApiDate(filters.value.startDate);
  const endDate = toApiDate(filters.value.endDate);
  if (startDate) params.startDate = startDate;
  if (endDate) params.endDate = endDate;
  return params;
};

const fetch = () => {
  const hasStart = !!filters.value.startDate;
  const hasEnd = !!filters.value.endDate;
  if (hasStart !== hasEnd) {
    warningToast("Validation", "Please select both start and end dates");
    return;
  }
  if (hasStart && hasEnd && moment(filters.value.startDate).isAfter(moment(filters.value.endDate), "day")) {
    warningToast("Validation", "Start date must be before end date");
    return;
  }

  loading.value = true;
  api()
    .get(STATISTICS_PATH, { params: buildRequestParams() })
    .then((response) => {
      const body = unwrapPayload(response) as Record<string, unknown>;
      if (body && typeof body === "object") {
        stats.value = body as Record<string, number>;
        trendRows.value = normalizeTrend(body.trend);
        topReceivers.value = Array.isArray(body.topReceivers) ? body.topReceivers as TopReceiver[] : [];
        topErrors.value = Array.isArray(body.topErrors) ? body.topErrors as TopError[] : [];
      } else {
        clearResults();
      }
    })
    .catch((error) => {
      clearResults();
      const msg =
        error.response?.data?.message || error.message || "Failed to load notification statistics";
      errorToast("Error", msg);
    })
    .finally(() => {
      loading.value = false;
    });
};

const resetFilters = () => {
  filters.value.startDate = null;
  filters.value.endDate = null;
  fetch();
};

const refresh = () => fetch();

defineExpose({ refresh });

onMounted(() => fetch());
</script>

<style>
.stats-table th,
.stats-table td {
  text-align: left !important;
}

.stats-table th:first-child,
.stats-table td:first-child {
  width: auto !important;
  position: static !important;
}

.stats-table th:nth-child(2),
.stats-table td:nth-child(2) {
  position: static !important;
}
</style>
